import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CustomTable, {
  type CustomTableColumn,
} from "components/common/CustomTable";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useToast } from "contexts/ToastContext";
import { useFaxes } from "hooks/useFaxes";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  formatFaxDate,
  renderStatusChip,
  triggerBlobDownload,
} from "utils/fax.utils";

import { faxesApi } from "../../../apis";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import type { FaxRecord } from "../../../types/fax";
import AssignFaxToPatientModal from "./AssignFaxToPatientModal";
import EFaxFilters from "./EFaxFilters";
import EFaxPracticeSelector from "./EFaxPracticeSelector";
import EFaxRowActions from "./EFaxRowActions";

interface FaxFiltersState {
  practiceId: string | null;
  search: string;
  dateFrom: string | null;
  dateTo: string | null;
  page: number;
  pageSize: number;
}

const EFaxList: React.FC = () => {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FaxFiltersState>(() => ({
    practiceId: searchParams.get("practiceId"),
    search: searchParams.get("search") || "",
    dateFrom: searchParams.get("dateFrom"),
    dateTo: searchParams.get("dateTo"),
    page: parseInt(searchParams.get("page") || "0", 10),
    pageSize: parseInt(searchParams.get("pageSize") || "10", 10),
  }));

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [assignTarget, setAssignTarget] = useState<FaxRecord | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (filters.practiceId) next.set("practiceId", filters.practiceId);
    if (filters.search) next.set("search", filters.search);
    if (filters.dateFrom) next.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) next.set("dateTo", filters.dateTo);
    if (filters.page) next.set("page", String(filters.page));
    if (filters.pageSize !== 10) next.set("pageSize", String(filters.pageSize));
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  const { items, total, isLoading, refetch } = useFaxes({
    practiceId: filters.practiceId ?? undefined,
    page: filters.page,
    pageSize: filters.pageSize,
    fromNumber: debouncedSearch || undefined,
    fromDate: filters.dateFrom || undefined,
    toDate: filters.dateTo || undefined,
  });

  const handlePracticeChange = useCallback((practiceId: string | null) => {
    setFilters((prev) => ({ ...prev, practiceId, page: 0 }));
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({ ...prev, search: e.target.value, page: 0 }));
    },
    [],
  );

  const handleDateChange = useCallback(
    (dateFrom: string | null, dateTo: string | null) => {
      setFilters((prev) => ({ ...prev, dateFrom, dateTo, page: 0 }));
    },
    [],
  );

  const handlePageChange = useCallback((_: unknown, page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handleRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({
        ...prev,
        pageSize: parseInt(e.target.value, 10),
        page: 0,
      }));
    },
    [],
  );

  const handleView = useCallback(
    async (fax: FaxRecord) => {
      try {
        const { signedUrl } = await faxesApi.getInboundFaxUrl({
          id: fax.id,
          practiceId: filters.practiceId ?? undefined,
        });
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      } catch {
        showError("Failed to open fax");
      }
    },
    [filters.practiceId, showError],
  );

  const handleDownload = useCallback(
    async (fax: FaxRecord) => {
      try {
        const { signedUrl } = await faxesApi.getInboundFaxUrl({
          id: fax.id,
          practiceId: filters.practiceId ?? undefined,
        });
        const resp = await fetch(signedUrl);
        const blob = await resp.blob();
        triggerBlobDownload(blob, `fax-${fax.id}.pdf`);
        showSuccess("Fax downloaded");
      } catch {
        showError("Failed to download fax");
      }
    },
    [filters.practiceId, showError, showSuccess],
  );

  const columns: CustomTableColumn<FaxRecord>[] = useMemo(
    () => [
      {
        id: "fromNumber",
        label: "From",
        getValue: (row) => row.fromNumber,
        width: "16%",
      },
      {
        id: "toNumber",
        label: "To",
        getValue: (row) => row.toNumber,
        width: "16%",
      },
      {
        id: "receivedAt",
        label: "Date",
        getValue: (row) => formatFaxDate(row.receivedAt),
        width: "18%",
      },
      {
        id: "numberOfPages",
        label: "Page(s)",
        getValue: (row) => row.numberOfPages ?? "—",
        width: "8%",
      },
      {
        id: "status",
        label: "Status",
        getValue: (row) => renderStatusChip(row.status),
        width: "12%",
      },
      {
        id: "assignment",
        label: "Assigned",
        getValue: (row) => (row.patientId ? "Assigned" : "—"),
        width: "12%",
      },
      {
        id: "actions",
        label: "",
        align: "center",
        width: "5%",
        getValue: (row) => (
          <EFaxRowActions
            onView={() => handleView(row)}
            onDownload={() => handleDownload(row)}
            onAssign={() => setAssignTarget(row)}
          />
        ),
      },
    ],
    [handleView, handleDownload],
  );

  return (
    <DashboardLayout title="eFax">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box className="flex flex-col h-full">
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${colors.border.light}`,
              borderRadius: 1,
              p: 2,
            }}
          >
            <Box className="mb-4 flex flex-wrap gap-4 items-stretch justify-between">
              <Box className="flex flex-wrap gap-4 items-stretch">
                <EFaxPracticeSelector
                  value={filters.practiceId}
                  onChange={handlePracticeChange}
                />
                <Box className="flex-1 min-w-[240px]">
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search"
                    value={filters.search}
                    onChange={handleSearchChange}
                    disabled={!filters.practiceId}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon className="text-gray-400" />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    }}
                  />
                </Box>
              </Box>
              <Box className="flex justify-end">
                <EFaxFilters
                  dateFrom={filters.dateFrom}
                  dateTo={filters.dateTo}
                  onChange={handleDateChange}
                />
              </Box>
            </Box>

            {filters.practiceId ? (
              <CustomTable<FaxRecord>
                data={items}
                columns={columns}
                totalCount={total}
                page={filters.page}
                rowsPerPage={filters.pageSize}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                isLoading={isLoading}
                loadingMessage="Loading faxes..."
                tableContainerSx={{ height: "calc(100vh - 250px)" }}
                getRowKey={(row) => row.id}
              />
            ) : (
              <Box
                className="flex items-center justify-center"
                sx={{ height: "calc(100vh - 250px)" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Select a practice to view its inbound fax inbox.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <AssignFaxToPatientModal
          fax={assignTarget}
          practiceId={filters.practiceId}
          open={Boolean(assignTarget)}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            setAssignTarget(null);
            refetch();
          }}
        />
      </LocalizationProvider>
    </DashboardLayout>
  );
};

export default EFaxList;
